import * as React from "react"
import { registerLocale } from "react-datepicker"
import { enUS as EnglishCalendar, es as SpanishCalendar } from "date-fns/locale"
import English from "assets/lang/en.json"
import Spanish from "assets/lang/es.json"
import { getStoredLanguage, setStoredLanguage } from "./language"
import {
	allowedLanguages,
	LANGUAGE_EN,
	LANGUAGE_ES,
	LanguageDictionary,
	LANGUAGES,
} from "constants/language"
import { MARKETS } from "constants/market"
import { RequestStopper } from "clients/utils"
import { SessionDataStore } from "helpers/authUtils"
import { initialState } from "components/Shared/FiltersContainer/reducer"
import {
	CUSTOMER_HABITS_METRIC_SESSION,
	REGION_SESSION,
	SHOW_EXPIRATION_MODAL_TIME,
} from "constants/session"
import { MetricType } from "../components/CustomerBehavior/Habits/types"
import {
	CUSTOMER_HABITS,
	FREQUENCY,
	HABITS,
	RETENTION,
	TICKET_INFORMATION,
} from "../components/CustomerBehavior/Habits/constants"

registerLocale(LANGUAGE_EN, EnglishCalendar)
registerLocale(LANGUAGE_ES, SpanishCalendar)

interface ApplicationContextInterface {
	collapseSideBar: boolean
	setCollapseSideBar: (isCollapseSideBar: boolean) => void
	messages: any
	setMessages: (messages: any) => void
	locale: string
	setLocale: (locale: string) => void
	allowedLocales: LanguageDictionary
	setAllowedLocales: (locale: LanguageDictionary) => void
	market: string
	setMarket: (market: string) => void
	isAuthenticated: boolean
	setIsAuthenticated: (isAuthenticated: boolean) => void
	showErrorModal: boolean
	setShowErrorModal: (isShowErrorModal: boolean) => void
	showSampleDataAlerts: boolean
	setShowSampleDataAlerts: (isShowSampleDataAlerts: boolean) => void
	selectLanguage: (language: string) => void
	selectMarket: (market: string) => void
	setIsLoading: (isLoading: boolean) => void
	isLoading: boolean
	requestStopper: RequestStopper | undefined
	setRequestStopper: (requestStopper: RequestStopper) => void
	isResetFilter: boolean
	setIsResetFilter: (isResetFilter: boolean) => void
	username: string
	setUsername: (username: string) => void
	isSupplier: boolean
	setIsSupplier: (isSupplier: boolean) => void
	userInitials: string
	setUserInitials: (userInitials: string) => void
	userRoles: string[]
	setUserRoles: (userRoles: Array<string>) => void
	regionParams: any
	setRegionParams: (params: any) => void
	customerHabitsDefaultMetric: MetricType
	expirationMessage: boolean
	setExpirationMessage: (params: boolean) => void
	welcomeMessage: boolean
	setWelcomeMessage: (params: boolean) => void
	setCustomerHabitsDefaultMetric: (
		customerHabitsDefaultMetric: MetricType
	) => void
	medalliaLastUpdate: string
	setMedalliaLastUpdate: (medallia: string) => void
	lastAcceptedTerms: string
	setLastAcceptedTerms: (lastAcceptedTerms: string) => void
	email: string
	setEmail: (email: string) => void
	plans: Array<string>
	setPlans: (plans: Array<string>) => void
	organizationName: string
}

export const collapseSideBar = () => {
	const { setCollapseSideBar } = useApplicationContext()
	React.useEffect(() => {
		setCollapseSideBar(true)
	}, [])
}

export const unCollapseSideBar = () => {
	const { setCollapseSideBar } = useApplicationContext()
	React.useEffect(() => {
		setCollapseSideBar(false)
	}, [])
}

export const PrepareContext = (): ApplicationContextInterface => {
	const [collapseSideBar, setCollapseSideBar] = React.useState<boolean>(false)
	const [isSupplier, setIsSupplier] = React.useState<boolean>(
		SessionDataStore.getInstance().getIsSupplier()
	)
	const [messages, setMessages] = React.useState<any>(Spanish)
	const [locale, setLocale] = React.useState<allowedLanguages>(LANGUAGE_ES)
	const [allowedLocales, setAllowedLocales] = React.useState<
		LanguageDictionary
	>(LANGUAGES)
	const [market, setMarket] = React.useState<string>(MARKETS.CHILE)
	const [username, setUsername] = React.useState<string>(
		SessionDataStore.getInstance().getUsername()
	)
	const [userInitials, setUserInitials] = React.useState<string>(
		SessionDataStore.getInstance().getUserInitials()
	)
	const [plans, setPlans] = React.useState<string[]>(
		SessionDataStore.getInstance().getPlans()
	)
	const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(
		SessionDataStore.getInstance().isAuthenticated()
	)
	const [userRoles, setUserRoles] = React.useState<string[]>(
		SessionDataStore.getInstance().getRoles()
	)
	const [showErrorModal, setShowErrorModal] = React.useState<boolean>(false)
	const [showSampleDataAlerts, setShowSampleDataAlerts] = React.useState<
		boolean
	>(true)
	const [expirationMessage, setExpirationMessage] = React.useState<boolean>(
		false
	)

	const [welcomeMessage, setWelcomeMessage] = React.useState<boolean>(true)

	const [isLoading, setIsLoading] = React.useState<boolean>(false)

	const [requestStopper, setRequestStopper] = React.useState<
		RequestStopper | undefined
	>()
	const [email, setEmail] = React.useState<string>("")
	const [lastAcceptedTerms, setLastAcceptedTerms] = React.useState<string>("")
	const [medalliaLastUpdate, setMedalliaLastUpdate] = React.useState<string>("")
	const organizationName = SessionDataStore.getInstance().getOrganizationName()

	React.useEffect(() => {
		const storedLanguage: string | null = getStoredLanguage()
		const storedLanguageOrDefault: allowedLanguages = storedLanguage || locale
		setLanguage(storedLanguageOrDefault)
	}, [])

	const setLanguage = (language: allowedLanguages) => {
		setLocale(language)
		if (language === LANGUAGE_ES) {
			setMessages(Spanish)
		} else {
			setMessages(English)
		}
		setStoredLanguage(language)
	}

	const selectLanguage = (newLocale: string) => {
		setLanguage(newLocale)
	}

	const selectMarket = (newMarket: string) => {
		setMarket(newMarket)
	}

	const [isResetFilter, setIsResetFilter] = React.useState<boolean>(false)

	const setRegionParams = (params: any) =>
		sessionStorage.setItem(REGION_SESSION, JSON.stringify(params))

	const regionParams = sessionStorage.getItem(REGION_SESSION)
		? sessionStorage.getItem(REGION_SESSION)
		: JSON.stringify(initialState.regions[0])

	const setDefaultCustomerHabitsMetric = (): MetricType => {
		const metricStorage = JSON.parse(
			localStorage.getItem(CUSTOMER_HABITS_METRIC_SESSION) || "{}"
		)
		const initialState = metricStorage[CUSTOMER_HABITS]
		return !initialState ? RETENTION : initialState
	}

	const customerHabitsDefaultMetric: MetricType = setDefaultCustomerHabitsMetric()

	const setCustomerHabitsDefaultMetric = (
		customerHabitsDefaultMetric: MetricType
	) => {
		localStorage.setItem(CUSTOMER_HABITS, customerHabitsDefaultMetric)
		const submodule =
			customerHabitsDefaultMetric == HABITS ? FREQUENCY : TICKET_INFORMATION
		localStorage.setItem(
			CUSTOMER_HABITS_METRIC_SESSION,
			JSON.stringify({
				customerhabits: customerHabitsDefaultMetric,
				habits: submodule,
			})
		)
	}

	React.useEffect(() => {
		const showExpirationMessage = () => {
			setExpirationMessage(true)
		}
		isAuthenticated &&
			setInterval(showExpirationMessage, SHOW_EXPIRATION_MODAL_TIME)
	}, [isAuthenticated])

	return {
		collapseSideBar,
		setCollapseSideBar,
		messages,
		setMessages,
		locale,
		setLocale,
		allowedLocales,
		setAllowedLocales,
		market,
		setMarket,
		isAuthenticated,
		setIsAuthenticated,
		showErrorModal,
		setShowErrorModal,
		showSampleDataAlerts,
		setShowSampleDataAlerts,
		isLoading,
		setIsLoading,
		selectLanguage,
		selectMarket,
		requestStopper,
		setRequestStopper,
		isResetFilter,
		setIsResetFilter,
		username,
		setUsername,
		userInitials,
		setUserInitials,
		userRoles,
		setUserRoles,
		regionParams,
		setRegionParams,
		customerHabitsDefaultMetric,
		setCustomerHabitsDefaultMetric,
		isSupplier,
		setIsSupplier,
		expirationMessage,
		setExpirationMessage,
		welcomeMessage,
		setWelcomeMessage,
		email,
		setEmail,
		medalliaLastUpdate,
		setMedalliaLastUpdate,
		lastAcceptedTerms,
		setLastAcceptedTerms,
		plans,
		setPlans,
		organizationName,
	}
}

export const ApplicationContext = React.createContext<ApplicationContextInterface | null>(
	null
)

export const useApplicationContext = () =>
	React.useContext(ApplicationContext) as ApplicationContextInterface
